import {DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {issuePath} from '@github-ui/paths'
import {useMutation} from '@github-ui/react-query'
import {deleteIssueFieldValueMutation as deleteIssueFieldValueQueryId} from '@github-ui/server-persisted-queries'
import type {
  DeleteIssueFieldValueInput,
  DeleteIssueFieldValueMutationMutation,
} from '@github-ui/server-persisted-queries/types/deleteIssueFieldValueMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import type {Disposable, Environment} from 'relay-runtime'

import {removeIssueFieldValue} from './helpers'
import {
  applyOptimisticIssueFieldValueRemovalToRelay,
  commitIssueFieldValueRemovalToRelay,
} from './issue-field-value-relay-bridge'

const DELETE_ERROR = 'Delete issue field value failed'
let deleteFieldValueMutationOperationSequence = 0

function nextDeleteIssueFieldValueOperationId() {
  return `delete-issue-field-value-${deleteFieldValueMutationOperationSequence++}`
}

type DeleteIssueFieldValueMutationInput = {
  issueId: Extract<DeleteIssueFieldValueInput['issueId'], string>
  fieldId: Extract<DeleteIssueFieldValueInput['fieldId'], string>
  fieldName: string
  owner: string
  repo: string
  number: number
}

type DeleteIssueFieldValueMutationOptions = {
  input: DeleteIssueFieldValueMutationInput
  onCompleted?: () => void
  onError?: (error?: Error) => void
}

type DeleteIssueFieldValueMutationVariables = {
  operation: ReturnType<typeof removeIssueFieldValue>
  operationId: string
  request: DeleteIssueFieldValueMutationInput
}

type MutationContext = {relayUpdate?: Disposable}
type DeleteResponse = {data?: DeleteIssueFieldValueMutationMutation | null; errors?: readonly unknown[] | null}

export async function executeNativeDeleteIssueFieldValueMutation(
  input: DeleteIssueFieldValueMutationInput,
): Promise<void> {
  const query = new URLSearchParams({queryId: deleteIssueFieldValueQueryId})
  const path = `${issuePath({
    owner: input.owner,
    repo: input.repo,
    issueNumber: input.number,
  })}/field_value.json`
  const response = await reactFetchJSON(`${path}?${query.toString()}`, {
    method: 'DELETE',
    body: {fieldId: input.fieldId},
  })
  if (!response.ok) throw new Error(`${DELETE_ERROR} (${response.status})`)

  let json: DeleteResponse
  try {
    json = (await response.json()) as DeleteResponse
  } catch {
    throw new Error(`${DELETE_ERROR}: response was invalid`)
  }

  const message = json.errors?.length
    ? json.errors
        .flatMap(error =>
          error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
            ? [error.message]
            : [],
        )
        .join(', ')
    : undefined
  if (!json.data) throw new Error(message || `${DELETE_ERROR}: deleteIssueFieldValue data was missing`)

  const payload = json.data.deleteIssueFieldValue
  if (!payload) throw new Error(message || `${DELETE_ERROR}: payload was missing`)
  if (!payload.issue) throw new Error(message || `${DELETE_ERROR}: issue was missing`)
  if (payload.issue.id !== input.issueId) throw new Error(message || `${DELETE_ERROR}: issue did not match`)
  if (!payload.success) throw new Error(message || `${DELETE_ERROR}: delete did not succeed`)
}

async function settleIssueFieldValueRemoval(
  environment: Environment,
  request: DeleteIssueFieldValueMutationInput,
  operation: ReturnType<typeof removeIssueFieldValue>,
  operationId: string,
  context: MutationContext | undefined,
) {
  try {
    commitIssueFieldValueRemovalToRelay(environment, request.issueId, request.fieldId)
    await notifySettlement(operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH, operationId)
  } finally {
    context?.relayUpdate?.dispose()
  }
}

export function useDeleteIssueFieldValueMutation() {
  const environment = useRelayEnvironment()
  const {mutateAsync} = useMutation<void, Error, DeleteIssueFieldValueMutationVariables, MutationContext>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: ({request}) => executeNativeDeleteIssueFieldValueMutation(request),
    onMutate: ({request}) => ({
      relayUpdate: applyOptimisticIssueFieldValueRemovalToRelay(environment, request.issueId, request.fieldId),
    }),
    onSuccess: (_, {request, operation, operationId}, context) =>
      settleIssueFieldValueRemoval(environment, request, operation, operationId, context),
    onError: async (_error, _variables, context) => {
      context?.relayUpdate?.dispose()
    },
  })

  return useCallback(
    (options: DeleteIssueFieldValueMutationOptions) => {
      const operationId = nextDeleteIssueFieldValueOperationId()
      const operation = removeIssueFieldValue(options.input.issueId, options.input.fieldId, options.input.fieldName)
      void (async () => {
        try {
          await mutateAsync({operation, operationId, request: options.input})
          options.onCompleted?.()
        } catch (error) {
          options.onError?.(error instanceof Error ? error : new Error(DELETE_ERROR))
        }
      })()
    },
    [mutateAsync],
  )
}
